import { Kekule } from '../../kekule/dist/kekule.min.js';

// src/components/solutionComposer.ts
function exportJSON(chemObj, condense = true) {
  var _a, _b, _c;
  function condenseMolecule(mol) {
    const molCopy = mol.clone(true);
    const connectorsToRemove = [];
    const nodesToRemove = [];
    for (var i = 0, l2 = molCopy.getNodeCount(); i < l2; ++i) {
      var node2 = molCopy.getNodeAt(i);
      if (node2 instanceof Kekule.Atom) {
        const linkedHydrogenAtoms = node2.getLinkedHydrogenAtoms();
        const pureLinkedHydrogenAtoms = linkedHydrogenAtoms.filter((hNode) => {
          return hNode.getLinkedConnectorCount() == 1 && hNode.getCharge() == 0 && (hNode.getRadical() == void 0 || hNode.getRadical() == 0);
        });
        const theoreticValence = node2.getIsotope().getTheoreticValence();
        const nonHydrogenConnectorsAmount = node2.getLinkedNonHydrogenConnectors().length;
        let hydrogenConnectionsLeft = theoreticValence - nonHydrogenConnectorsAmount;
        for (let j2 = 0; j2 < pureLinkedHydrogenAtoms.length && hydrogenConnectionsLeft > 0; ++j2, --hydrogenConnectionsLeft) {
          connectorsToRemove.push(pureLinkedHydrogenAtoms[j2].getLinkedConnectorAt(0));
          nodesToRemove.push(pureLinkedHydrogenAtoms[j2]);
        }
      }
    }
    for (let i2 = 0; i2 < nodesToRemove.length; ++i2) {
      molCopy.removeNode(nodesToRemove[i2], false);
    }
    for (let i2 = 0; i2 < connectorsToRemove.length; ++i2) {
      molCopy.removeConnector(connectorsToRemove[i2], false);
    }
    return molCopy;
  }
  const json = {
    molecules: [],
    reactionArrows: [],
    texts: [],
    formulas: []
  };
  if (!chemObj || !chemObj.getChildren) {
    return null;
  }
  const children = chemObj.getChildren();
  for (let i = 0; i < children.length; i += 1) {
    let elem = children[i];
    if (!(elem instanceof Kekule.Molecule || elem instanceof Kekule.Glyph.ReactionArrow || elem instanceof Kekule.TextBlock)) {
      console.log(`Unknown element ${elem.getId()}:`, elem);
      continue;
    }
    if (elem instanceof Kekule.Molecule) {
      if (condense) {
        elem = condenseMolecule(elem);
      }
      const molecule = {
        id: elem.id,
        atoms: [],
        bonds: [],
        formula: void 0
      };
      if (elem.hasFormula()) {
        const formula = {
          id: elem.id,
          formulaAsText: elem.getFormula().getDisplayText(),
          charge: (_a = elem.charge) != null ? _a : void 0,
          radical: (_b = elem.radical) != null ? _b : void 0
        };
        json.formulas.push(formula);
        continue;
      }
      for (var j = 0, l = elem.getNodeCount(); j < l; ++j) {
        var node = elem.getNodeAt(j);
        if (node instanceof Kekule.Atom || node instanceof Kekule.Pseudoatom) {
          const atom = {
            id: node.getId(),
            charge: node.getCharge(),
            symbol: node.getSymbol(),
            radical: node.getRadical()
          };
          molecule.atoms.push(atom);
        }
      }
      for (var j = 0, l = elem.getConnectorCount(); j < l; ++j) {
        var connector = elem.getConnectorAt(j);
        if (connector instanceof Kekule.Bond) {
          const bond = {
            id: connector.getId(),
            type: connector.getBondType(),
            order: connector.getBondOrder(),
            stereo: connector.getStereo(),
            connects: []
          };
          const connectedTo = connector.getConnectedChemNodes();
          for (let k = 0, m = connectedTo.length; k < m; ++k) {
            const connected = connectedTo[k];
            bond.connects.push(connected.getId());
          }
          molecule.bonds.push(bond);
        }
      }
      if (elem.calcFormula) {
        const formula = elem.calcFormula().getDisplayRichText();
        formula.items.sort((a, b) => {
          var _a2, _b2;
          a = ((_a2 = a.items[0].anchorItem) != null ? _a2 : a.anchorItem).text;
          b = ((_b2 = b.items[0].anchorItem) != null ? _b2 : b.anchorItem).text;
          if (a == "C" || a == "H" && b != "C") {
            return -1;
          }
          if (b == "C" || b == "H" && a != "C") {
            return 1;
          }
          return a.localeCompare(b);
        });
        let stringFormula = "";
        for (let i2 = 0; i2 < formula.items.length; ++i2) {
          const atom = formula.items[i2];
          let anchorItem = (_c = atom.items[0].anchorItem) != null ? _c : atom.anchorItem;
          stringFormula += `${anchorItem.text}`;
          for (let j2 = 1; j2 < atom.items.length; ++j2) {
            const atomPart = atom.items[j2];
            stringFormula += `${atomPart.text}`;
          }
        }
        molecule.formula = stringFormula;
      }
      json.molecules.push(molecule);
    }
    if (elem instanceof Kekule.Glyph.ReactionArrow) {
      const reactionArrow = {
        id: elem.id,
        reactionType: elem.reactionType
      };
      json.reactionArrows.push(reactionArrow);
    }
    if (elem instanceof Kekule.Glyph.ReactionArrow) {
      const text = {
        id: elem.id,
        text: elem.text
      };
      json.texts.push(text);
    }
  }
  return JSON.stringify(json);
}

// src/components/solutionComposer.ts
Kekule.ZoomUtils.PREDEFINED_ZOOM_RATIOS = [1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5, 7, 9];
var Composer = class {
  /**
   * Creates a new default composer and mounts it on htmlElement
   * 
   * @param htmlElement the html element on which to mount the composer
   * @param {Logger.Logger} logger the logger
   */
  constructor(htmlElement, logger, type = "student") {
    this.scroll = { isScrolling: false, lastPointerCoord: { x: 0, y: 0 }, previousCursorStyle: "" };
    this.kekuleComposer = new Kekule.Editor.Composer(htmlElement);
    this.type = type;
    const style = getComputedStyle(document.body);
    style.getPropertyValue("--primary-color");
    const secondaryColor = style.getPropertyValue("--secondary-color");
    style.getPropertyValue("--tertiary-color");
    const chemSpaceConfigs = this.kekuleComposer.getEditor().getDisplayerConfigs().getChemSpaceConfigs();
    chemSpaceConfigs.setDefScreenSize2D({ x: htmlElement.clientWidth, y: htmlElement.clientHeight });
    const interactionConfigs = this.kekuleComposer.getEditor().getDisplayerConfigs().getInteractionConfigs();
    interactionConfigs.setAutoSelectNewlyInsertedObjectsOnTouch(false);
    interactionConfigs.setEditorInitialZoom(3);
    const uiMarkerConfigs = this.kekuleComposer.getEditor().getDisplayerConfigs().getUiMarkerConfigs();
    uiMarkerConfigs.setHotTrackerColor(secondaryColor);
    uiMarkerConfigs.setHotTrackerOpacity(0.5);
    uiMarkerConfigs.setSelectingBrushMarkerStrokeColor(secondaryColor);
    uiMarkerConfigs.setSelectingBrushMarkerOpacity(0.6);
    uiMarkerConfigs.setSelectionMarkerEmphasisOpacity(0.8);
    uiMarkerConfigs.setSelectionMarkerFillColor(secondaryColor);
    uiMarkerConfigs.setSelectionMarkerStrokeColor(secondaryColor);
    uiMarkerConfigs.setSelectionMarkerOpacity(0.4);
    this.kekuleComposer.setShowInspector(false);
    this._enableAutoSave();
    this._changeInitialButtons(navigator.maxTouchPoints != 0);
    this._addRightClickScroll();
    this._changeHotkeys();
    const currentPath = window.location.hash;
    const autoSavedKCJ = localStorage.getItem(`${currentPath}-composerKCJ`);
    if (autoSavedKCJ != null) {
      this.importKCJ(autoSavedKCJ);
    } else {
      this.kekuleComposer.newDoc();
    }
    this._centerInitialDisplay();
    this._addFormula();
    if (logger) {
      this._setupLogger(logger);
    }
  }
  _enableAutoSave() {
    const currentPath = window.location.hash;
    this.kekuleComposer.on("userModificationDone", (e) => {
      localStorage.setItem(`${currentPath}-composerKCJ`, this.exportKCJ());
    });
  }
  /**
   * Get formula of drawn molecules (text)
   * 
   * @returns Array of formulas (string), one formula per drawn molecule
   */
  getFormulaAsText() {
    const formula = [];
    const children = this.kekuleComposer.getEditor().getChemObj().getChildren();
    for (let i = 0; i < children.length; i += 1) {
      const elem = children[i];
      if (elem.calcFormula) {
        formula.push(elem.calcFormula().getDisplayText());
      }
    }
    return formula;
  }
  /**
   * Get formula of drawn molecules (div)
   * 
   * @returns Array of formulas (div), one formula per drawn molecule
   */
  getFormulaAsDiv() {
    function createFormattedDivs(formula2) {
      var _a;
      const returnDiv = document.createElement("div");
      returnDiv.className = "Formula";
      formula2.items.sort((a, b) => {
        var _a2, _b;
        a = ((_a2 = a.items[0].anchorItem) != null ? _a2 : a.anchorItem).text;
        b = ((_b = b.items[0].anchorItem) != null ? _b : b.anchorItem).text;
        if (a == "C" || a == "H" && b != "C") {
          return -1;
        }
        if (b == "C" || b == "H" && a != "C") {
          return 1;
        }
        return a.localeCompare(b);
      });
      for (let i = 0, l = formula2.items.length; i < l; ++i) {
        const atom = formula2.items[i];
        let anchorItem = (_a = atom.items[0].anchorItem) != null ? _a : atom.anchorItem;
        returnDiv.appendChild(document.createTextNode(anchorItem.text));
        for (let j = 1, l2 = atom.items.length; j < l2; ++j) {
          const atomPart = atom.items[j];
          if (atomPart.textType == "subscript") {
            const sub = document.createElement("sub");
            sub.textContent = atomPart.text;
            returnDiv.appendChild(sub);
          }
          if (atomPart.textType == "superscript") {
            const sup = document.createElement("sup");
            sup.textContent = atomPart.text;
            returnDiv.appendChild(sup);
          }
        }
      }
      return returnDiv;
    }
    const formula = [];
    const children = this.kekuleComposer.getEditor().getChemObj().getChildren();
    for (let i = 0; i < children.length; i += 1) {
      const elem = children[i];
      if (elem.calcFormula) {
        formula.push(createFormattedDivs(elem.calcFormula().getDisplayRichText()));
      }
    }
    return formula;
  }
  importKCJ(kcj) {
    try {
      this.kekuleComposer.setChemObj(Kekule.IO.loadFormatData(kcj, Kekule.IO.DataFormat.KEKULE_JSON));
    } catch (error) {
      console.log(`Error while importing KCJ`, error);
    }
  }
  exportKCJ() {
    return Kekule.IO.saveFormatData(this.kekuleComposer.getChemObj(), Kekule.IO.DataFormat.KEKULE_JSON);
  }
  getMoleculeByID(id) {
    return this.kekuleComposer.getChemObj();
  }
  clearContent() {
    this.kekuleComposer.newDoc();
    this._centerInitialDisplay();
  }
  /**
   * Returns composer content as JSON. Returned string can be parsed with 
   * JSON.parse() to get a {@link JsonExport.Json}.
   * 
   * @returns string containing composer content as a json. Null if error.
   */
  exportJSON() {
    return exportJSON(this.kekuleComposer.getEditor().getChemObj());
  }
  /**
   * Download composer content as png.
   * 
   */
  downloadImage() {
    const boundingBox = this.kekuleComposer.getEditor().getObjectsContainerBox(this.kekuleComposer.getEditor().getChemObj().getChildren());
    if (!boundingBox) {
      return;
    }
    const xMin = boundingBox.x1, xMax = boundingBox.x2, yMin = boundingBox.y1, yMax = boundingBox.y2;
    const canvas = this.kekuleComposer.getEditor().getObjContext().canvas;
    const padding = 20;
    var croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = xMax - xMin + padding;
    croppedCanvas.height = yMax - yMin + padding;
    croppedCanvas.getContext("2d").drawImage(canvas, xMin - padding / 2, yMin - padding / 2, xMax + padding / 2, yMax + padding / 2, 0, 0, xMax, yMax);
    const imgUrl = croppedCanvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = imgUrl;
    link.download = "composer.png";
    link.click();
    croppedCanvas.remove();
    link.remove();
  }
  /**
   * Set up logging (also sends first logStart).
   * 
   * @param logger the logger
   */
  _setupLogger(logger) {
    logger.logStart({
      composerContent: this.exportKCJ()
    });
    this.kekuleComposer.on("userModificationDone", (e) => {
      const drawingChangeEvent = {
        composerContent: this.exportKCJ()
      };
      logger.logDrawingChange(drawingChangeEvent);
    });
    this.kekuleComposer.on("load", (e) => {
      let type = "unknown";
      let data = null;
      if (e.obj.srcInfo.dataType) {
        const srcInfo = e.obj.getSrcInfo();
        type = srcInfo.dataType == "text" ? "text" : "file";
        if (type == "file") {
          data = {
            type: srcInfo.dataType,
            name: srcInfo.fileName
          };
        } else {
          data = {
            format: srcInfo.format,
            mimetype: srcInfo.mimeType
          };
        }
      }
      const loadEvent = {
        type,
        data,
        composerContent: this.exportKCJ()
      };
      logger.logLoad(loadEvent);
    });
  }
  _addZoomButtons() {
    const ZOOMBAR_CLASS = "C-Chem-Composer-Zoombar";
    const ZOOMBAR_SPAN_CLASS = "C-Chem-Composer-Zoombar-Span";
    const ZOOM_IN_BTN_CLASS = "C-Zoom-In";
    const ZOOM_OUT_BTN_CLASS = "C-Zoom-Out";
    const zoomInBtn = document.createElement("button");
    zoomInBtn.classList.add("K-Widget", "K-Style-Undependent", "K-Button", "K-NonSelectable", "K-No-Wrap", "K-Text-Hide", "K-Glyph-Show", "K-Layout-V", ZOOM_IN_BTN_CLASS);
    zoomInBtn.type = "button";
    zoomInBtn.tabIndex = 0;
    zoomInBtn.title = "Zoom in";
    zoomInBtn.onclick = () => this.kekuleComposer.execBoundEditorAction("zoomIn");
    const zoomOutBtn = document.createElement("button");
    zoomOutBtn.classList.add("K-Widget", "K-Style-Undependent", "K-Button", "K-NonSelectable", "K-No-Wrap", "K-Text-Hide", "K-Glyph-Show", "K-Layout-V", ZOOM_OUT_BTN_CLASS);
    zoomOutBtn.type = "button";
    zoomOutBtn.tabIndex = 0;
    zoomOutBtn.title = "Zoom out";
    zoomOutBtn.onclick = () => this.kekuleComposer.execBoundEditorAction("zoomOut");
    const glyphSpan = document.createElement("span");
    glyphSpan.classList.add("K-Content", "K-Glyph-Content", "K-Dynamic-Created", "K-Pri-Glyph-Content");
    zoomInBtn.appendChild(glyphSpan.cloneNode());
    zoomOutBtn.appendChild(glyphSpan);
    const zoomSpan = document.createElement("span");
    zoomSpan.classList.add(ZOOMBAR_SPAN_CLASS);
    zoomSpan.appendChild(zoomInBtn);
    zoomSpan.appendChild(zoomOutBtn);
    const zoomDiv = document.createElement("div");
    zoomDiv.classList.add(ZOOMBAR_CLASS, "K-Dynamic-Created");
    zoomDiv.appendChild(zoomSpan);
    const composerElement = this.kekuleComposer.getElement();
    composerElement.appendChild(zoomDiv);
  }
  /**
   * Adds a div in bottom right corner of composer that shows the formula of currently drawn molecules. Also adds 
   * event listener on editor to refresh formulas when editor changes.
   * 
   */
  _addFormula() {
    const formulaDivClass = "FormulaDiv";
    const kekuleHtmlElement = this.kekuleComposer.getElement();
    let formulaDiv = document.createElement("div");
    formulaDiv.className = formulaDivClass;
    kekuleHtmlElement.appendChild(formulaDiv);
    this.kekuleComposer.getEditor().on("editObjsUpdated", (e) => {
      formulaDiv.innerHTML = "";
      const formulas = this.getFormulaAsDiv();
      for (let i = 0; i < formulas.length; i += 1) {
        formulaDiv.appendChild(formulas[i]);
      }
    });
    this.kekuleComposer.getEditor().on("load", (e) => {
      formulaDiv.innerHTML = "";
      const formulas = this.getFormulaAsDiv();
      for (let i = 0; i < formulas.length; i += 1) {
        formulaDiv.appendChild(formulas[i]);
      }
    });
  }
  _changeHotkeys() {
    const hotkeys = this.kekuleComposer.getEditorConfigs().getHotKeyConfigs().getHotKeys();
    const ctrlshiftZindex = hotkeys.findIndex((hotkey) => {
      return hotkey.key == "Ctrl+Shift+Z";
    });
    if (ctrlshiftZindex != -1) {
      hotkeys[ctrlshiftZindex].key = "Ctrl+Y";
    }
  }
  /**
   * By default in kekule, scrolling is a button in the toolbar. 
   * Adds a scroll functionality on right click mouse button.
   */
  _addRightClickScroll() {
    this.kekuleComposer.getEditor().getUiEventReceiverElem().addEventListener("contextmenu", (e) => {
      e.preventDefault();
    });
    this.kekuleComposer.getEditor().getUiEventReceiverElem().addEventListener("pointerdown", (e) => {
      if (e.pointerType == "mouse" && e.button == Kekule.X.Event.MouseButton.RIGHT) {
        this.scroll.isScrolling = true;
        this.scroll.lastPointerCoord = { x: e.clientX, y: e.clientY };
      }
    });
    this.kekuleComposer.getEditor().getUiEventReceiverElem().addEventListener("pointerup", (e) => {
      if (e.pointerType == "mouse" && e.button == Kekule.X.Event.MouseButton.RIGHT) {
        this.scroll.isScrolling = false;
      }
    });
    this.kekuleComposer.getEditor().getUiEventReceiverElem().addEventListener("pointermove", (e) => {
      if (e.pointerType == "mouse" && this.scroll.isScrolling) {
        const clientViewScroll = this.kekuleComposer.getEditor().getClientScrollPosition();
        const currPointerPos = { x: e.clientX, y: e.clientY };
        const delta = Kekule.CoordUtils.substract(this.scroll.lastPointerCoord, currPointerPos);
        const newClientViewScroll = Kekule.CoordUtils.add(clientViewScroll, delta);
        this.kekuleComposer.getEditor().scrollClientToCoord(newClientViewScroll);
        this.scroll.lastPointerCoord = currPointerPos;
      }
    });
  }
  /**
   * Adds zoom commands on mouse wheel scroll up and down.
   */
  _addWheelZoom() {
    this.kekuleComposer.getEditor().getUiEventReceiverElem().addEventListener("wheel", (e) => {
      if (e.ctrlKey) {
        return;
      }
      if (e.deltaY > 0) {
        this.kekuleComposer.execBoundEditorAction("zoomOut");
      }
      if (e.deltaY < 0) {
        this.kekuleComposer.execBoundEditorAction("zoomIn");
      }
    });
  }
  _centerInitialDisplay() {
    var clientElem = this.kekuleComposer.getEditor().getEditClientElem();
    var clientElemDim = Kekule.HtmlElementUtils.getElemClientDimension(clientElem);
    setTimeout(() => {
      this.kekuleComposer.getEditor().scrollClientToCoord({ y: clientElemDim.height / 2, x: clientElemDim.width / 2 }, null, { scrollToCenter: true });
    });
  }
  /**
   * Change the initial buttons of the kekule chem composer. 
   * Changes: 
   *     - Remove Zoom buttons (if keepZoomButtons is set to false)
   *     - Remove Settings
   *     - Remove Object inspector
   *     - Remove Create new document
   * 
   * Use 'https://partridgejiang.github.io/Kekule.js/demos/demoLauncher.html?id=composerCustomization' to customize.
   */
  _changeInitialButtons(keepZoomButtons) {
    var N = Kekule.ChemWidget.ComponentWidgetNames;
    Kekule.Editor.ObjModifier.Category;
    let commonToolButtons = [
      N.loadData,
      N.saveData,
      N.undo,
      N.redo,
      N.copy,
      N.cut,
      N.paste
    ];
    if (this.type == "student") ;
    this.kekuleComposer.setCommonToolButtons(commonToolButtons);
    let chemToolButtons = [
      {
        "name": N.manipulate,
        "attached": [
          N.manipulateMarquee,
          N.manipulateLasso,
          N.manipulateBrush,
          N.manipulateAncestor,
          N.toggleSelect
        ]
      },
      N.dragScroll,
      N.erase,
      {
        "name": N.molBond,
        "attached": [
          N.molBondSingle,
          N.molBondDouble,
          N.molBondTriple,
          N.molBondWedgeUp,
          N.molBondWedgeDown,
          N.molChain,
          N.trackInput,
          N.molRepFischer1,
          N.molRepSawhorseStaggered,
          N.molRepSawhorseEclipsed
        ]
      },
      {
        "name": N.molAtomAndFormula,
        "attached": this.type == "student" ? [
          N.molAtom
        ] : [
          N.molAtom,
          N.molFormula
        ]
      },
      {
        "name": N.molRing,
        "attached": [
          N.molRing3,
          N.molRing4,
          N.molRing5,
          N.molRing6,
          N.molFlexRing,
          N.molRingAr6,
          N.molRepCyclopentaneHaworth1,
          N.molRepCyclohexaneHaworth1,
          N.molRepCyclohexaneChair1,
          N.molRepCyclohexaneChair2
        ]
      },
      {
        "name": N.molCharge,
        "attached": [
          N.molChargeClear,
          N.molChargePositive,
          N.molChargeNegative,
          N.molRadicalSinglet,
          N.molRadicalTriplet,
          N.molRadicalDoublet,
          N.molElectronLonePair
        ]
      },
      {
        "name": N.glyph,
        "attached": [
          N.glyphReactionArrowNormal,
          N.glyphReactionArrowReversible,
          N.glyphReactionArrowResonance,
          N.glyphReactionArrowRetrosynthesis,
          N.glyphRepSegment,
          N.glyphElectronPushingArrowDouble,
          N.glyphElectronPushingArrowSingle,
          N.glyphRepHeatSymbol,
          N.glyphRepAddSymbol
        ]
      },
      {
        "name": N.textImage,
        "attached": [
          N.textBlock,
          N.imageBlock
        ]
      }
    ];
    if (this.type == "student") {
      chemToolButtons = chemToolButtons.slice(0, -2);
    }
    this.kekuleComposer.setChemToolButtons(chemToolButtons);
    const dwnldImgButtonClass = "C-Save-Image";
    const dwnldImgButton = document.createElement("button");
    dwnldImgButton.classList.add("K-Widget", "K-Style-Undependent", "K-Button", "K-NonSelectable", "K-No-Wrap", "K-Text-Hide", "K-Glyph-Show", "K-Layout-H", "K-Corner-Tailing", dwnldImgButtonClass);
    dwnldImgButton.type = "button";
    dwnldImgButton.tabIndex = 0;
    dwnldImgButton.title = "Save as image";
    dwnldImgButton.onclick = () => this.downloadImage();
    const glyphSpan = document.createElement("span");
    glyphSpan.classList.add("K-Content", "K-Glyph-Content", "K-Dynamic-Created", "K-Pri-Glyph-Content");
    dwnldImgButton.appendChild(glyphSpan);
    this.kekuleComposer.getTopRegionElem().children[0].appendChild(dwnldImgButton);
    if (keepZoomButtons) {
      this._addZoomButtons();
    }
  }
  /**
   * Used for dev
   */
  doSometing() {
    this.exportJSON();
  }
};
var IterationContext = class _IterationContext {
  constructor(atomMatchMap, bondMatchMap, testMolVisitedAtoms, controlMolVisitedAtoms) {
    this.matchMap = {
      atom: atomMatchMap,
      bond: bondMatchMap
    };
    this.visitedAtoms = {
      testMol: testMolVisitedAtoms,
      controlMol: controlMolVisitedAtoms
    };
    this.visitedBonds = {
      testMol: [],
      controlMol: []
    };
  }
  createCopy() {
    const atomMatchMapCopy = new Map(this.matchMap.atom);
    const bondMatchMapCopy = new Map(this.matchMap.bond);
    const testMolVisitedAtomsCopy = Array.from(this.visitedAtoms.testMol);
    const controlMolVisitedAtomsCopy = Array.from(this.visitedAtoms.controlMol);
    const testMolVisitedBondsCopy = Array.from(this.visitedBonds.testMol);
    const controlMolVisitedBondsCopy = Array.from(this.visitedBonds.controlMol);
    const newIterationContext = new _IterationContext(atomMatchMapCopy, bondMatchMapCopy, testMolVisitedAtomsCopy, controlMolVisitedAtomsCopy);
    newIterationContext.visitedBonds.testMol = testMolVisitedBondsCopy;
    newIterationContext.visitedBonds.controlMol = controlMolVisitedBondsCopy;
    return newIterationContext;
  }
  /*
   * Absorb another IterationContext object. The other IterationContext 
   * object's content is added to the current one.
   *
   */
  absorb(iterationContext) {
    for (const [atom, matchingAtom] of iterationContext.matchMap.atom) {
      this.matchMap.atom.set(atom, matchingAtom);
    }
    for (const [bond, matchingBond] of iterationContext.matchMap.bond) {
      this.matchMap.bond.set(bond, matchingBond);
    }
    this.visitedAtoms.testMol.push(...iterationContext.visitedAtoms.testMol);
    this.visitedAtoms.controlMol.push(...iterationContext.visitedAtoms.controlMol);
    this.visitedBonds.testMol.push(...iterationContext.visitedBonds.testMol);
    this.visitedBonds.controlMol.push(...iterationContext.visitedBonds.controlMol);
  }
};
var Neighbor = class {
  constructor(atom, bond) {
    this.atom = atom;
    this.bond = bond;
  }
};
var AtomNode = class {
  constructor(atom) {
    this.id = atom.id;
    this.charge = atom.charge;
    this.symbol = atom.symbol;
    this.radical = atom.radical;
    this.neighbors = [];
  }
  registerBond(bond, neighbor) {
    this.neighbors.push(new Neighbor(neighbor, bond));
  }
  isEquivalent(comparison) {
    return this.symbol == comparison.symbol && this.charge == comparison.charge && this.radical == comparison.radical;
  }
  hasAsNeighbor(atomNode) {
    for (const neighbor of this.neighbors) {
      if (neighbor.atom.id == atomNode.id) {
        return true;
      }
    }
    return false;
  }
  getNeighborWithAtom(atomNode) {
    for (const neighbor of this.neighbors) {
      if (neighbor.atom.id == atomNode.id) {
        return neighbor;
      }
    }
    return void 0;
  }
};
var BondNode = class {
  constructor(bond) {
    this.id = bond.id;
    this.type = bond.type;
    this.order = bond.order;
    this.stereo = bond.stereo;
    this.connects = [];
  }
  registerConnectedAtom(atom) {
    this.connects.push(atom);
  }
  isEquivalent(comparison) {
    return this.type == comparison.type && this.order == comparison.order && this.stereo == comparison.stereo;
  }
};
var MolGraph = class {
  /*
   * Molecule graph constructor.
   *
   * @throws Error
   * Molecule is not correct (bond connects an atom id that is not in the list of atoms)
   */
  constructor(mol) {
    this.atoms = /* @__PURE__ */ new Map();
    this.bonds = /* @__PURE__ */ new Map();
    for (const currAtom of mol.atoms) {
      this.atoms.set(currAtom.id, new AtomNode(currAtom));
    }
    for (const currBond of mol.bonds) {
      const bondNode = new BondNode(currBond);
      for (const connectedAtomId of currBond.connects) {
        const connectedAtomNode = this.atoms.get(connectedAtomId);
        if (!connectedAtomNode) {
          throw new Error("Molecule malformation: bond " + currBond.id + " connects atom " + connectedAtomId + " but it is not in the list of atoms.");
        }
        bondNode.registerConnectedAtom(connectedAtomNode);
        const connectedAtomNodeNeighbor = this.atoms.get(currBond.connects.filter((connect) => connect != connectedAtomId)[0]);
        if (!connectedAtomNodeNeighbor) {
          throw new Error("Molecule malformation: bond " + currBond.id + " connects atom " + connectedAtomId + " but it is not in the list of atoms.");
        }
        connectedAtomNode.registerBond(bondNode, connectedAtomNodeNeighbor);
      }
      this.bonds.set(currBond.id, bondNode);
    }
  }
  /**
   * Returns an array of AtomNode that are equivalent to 
   * atomNode (equivalent = same symbol and same amount of neighbors)
   * 
   * @param atomNode the model on which to find matches
   */
  getMatching(atomNode) {
    return Array.from(this.atoms.values()).filter((entry) => entry.symbol == atomNode.symbol && entry.neighbors.length == atomNode.neighbors.length);
  }
};
function k_combinations(set, k) {
  var i, j, combs, head, tailcombs;
  if (k > set.length || k <= 0) {
    return [];
  }
  if (k == set.length) {
    return [set];
  }
  if (k == 1) {
    combs = [];
    for (i = 0; i < set.length; i++) {
      combs.push([set[i]]);
    }
    return combs;
  }
  combs = [];
  for (i = 0; i < set.length - k + 1; i++) {
    head = set.slice(i, i + 1);
    tailcombs = k_combinations(set.slice(i + 1), k - 1);
    for (j = 0; j < tailcombs.length; j++) {
      combs.push(head.concat(tailcombs[j]));
    }
  }
  return combs;
}
function* permute(permutation) {
  var length = permutation.length;
  let c = Array(length).fill(0);
  let i = 1, k, p;
  yield permutation.slice();
  while (i < length) {
    if (c[i] < i) {
      k = i % 2 && c[i];
      p = permutation[i];
      permutation[i] = permutation[k];
      permutation[k] = p;
      ++c[i];
      i = 1;
      yield permutation.slice();
    } else {
      c[i] = 0;
      ++i;
    }
  }
}
function createMapFromArrays(array1, array2) {
  const resultMap = /* @__PURE__ */ new Map();
  for (let i = 0; i < array1.length; ++i) {
    resultMap.set(array1[i], array2[i]);
  }
  return resultMap;
}
function checkMapping(na, nb, iterationContext, match) {
  var _a, _b, _c, _d;
  const errors = [];
  for (const naNeighbor of na.atom.neighbors) {
    const matchingNaNeighborAtom = (_b = iterationContext.matchMap.atom.get(naNeighbor.atom)) != null ? _b : (_a = match.get(naNeighbor)) == null ? void 0 : _a.atom;
    if (matchingNaNeighborAtom) {
      if (nb.atom.hasAsNeighbor(matchingNaNeighborAtom)) {
        const matchingNaNeighbor = nb.atom.getNeighborWithAtom(matchingNaNeighborAtom);
        if (!matchingNaNeighbor.bond.isEquivalent(naNeighbor.bond)) {
          errors.push(naNeighbor.bond.id);
        }
      } else {
        errors.push(na.atom.id);
        errors.push(naNeighbor.atom.id);
      }
    }
  }
  const invertedAtomMatchMap = new Map(Array.from(iterationContext.matchMap.atom, (a) => a.reverse()));
  const invertedMatch = new Map(Array.from(match, (a) => a.reverse()));
  for (const nbNeighbor of nb.atom.neighbors) {
    const matchingNbNeighborAtom = (_d = invertedAtomMatchMap.get(nbNeighbor.atom)) != null ? _d : (_c = invertedMatch.get(nbNeighbor)) == null ? void 0 : _c.atom;
    if (matchingNbNeighborAtom) {
      if (na.atom.hasAsNeighbor(matchingNbNeighborAtom)) {
        const matchingNbNeighbor = na.atom.getNeighborWithAtom(matchingNbNeighborAtom);
        if (!matchingNbNeighbor.bond.isEquivalent(nbNeighbor.bond)) {
          errors.push(matchingNbNeighbor.bond.id);
        }
      } else {
        errors.push(na.atom.id);
        errors.push(matchingNbNeighborAtom.id);
      }
    }
  }
  return [...new Set(errors)];
}
function compareMoleculeFromMatch(testMolGraph, atomId, controlMolGraph, matchingAtomId) {
  const atom = testMolGraph.atoms.get(atomId);
  const matchingAtom = controlMolGraph.atoms.get(matchingAtomId);
  if (!atom || !matchingAtom) {
    throw new Error("Atom id(s) provided (" + atomId + " or/and " + matchingAtomId + ") not found in corresponding molecule.");
  }
  if (atom.neighbors.length == 0 && matchingAtom.neighbors.length == 0) {
    return [];
  }
  let bestComparison = void 0;
  for (let permutation of permute(atom.neighbors)) {
    if (permutation.length > 0) {
      const iterationContext = new IterationContext((/* @__PURE__ */ new Map()).set(atom, matchingAtom), /* @__PURE__ */ new Map(), [atom.id], [matchingAtom.id]);
      const testMatch = createMapFromArrays(permutation, matchingAtom.neighbors);
      let permutationResultingErrors = compareMoleculeFromNeighbors(iterationContext, testMatch, bestComparison == null ? void 0 : bestComparison.length);
      if (!permutationResultingErrors) {
        continue;
      }
      const nonVisitedAtomIds = Array.from(testMolGraph.atoms.values()).filter((currAtom) => !iterationContext.visitedAtoms.testMol.includes(currAtom.id)).map((nonVisitedAtomNode) => nonVisitedAtomNode.id);
      permutationResultingErrors.push(...nonVisitedAtomIds);
      for (const [neighbor, matchingNeighbor] of testMatch) {
        permutationResultingErrors.push(
          ...checkMapping(neighbor, matchingNeighbor, iterationContext, testMatch)
        );
      }
      permutationResultingErrors = [...new Set(permutationResultingErrors)];
      if (!bestComparison || bestComparison.length > permutationResultingErrors.length) {
        bestComparison = Array.from(permutationResultingErrors);
      }
    }
  }
  return bestComparison;
}
function compareMoleculeFromNeighbors(iterationContext, match, currentBest) {
  let errors = [];
  iterationContext.visitedAtoms.testMol.push(...Array.from(match.keys()).map((neighbor) => neighbor.atom.id));
  iterationContext.visitedAtoms.controlMol.push(...Array.from(match.values()).map((neighbor) => neighbor.atom.id));
  iterationContext.visitedBonds.testMol.push(...Array.from(match.keys()).map((neighbor) => neighbor.bond.id));
  iterationContext.visitedBonds.controlMol.push(...Array.from(match.values()).map((neighbor) => neighbor.bond.id));
  for (let [neighbor, matchingNeighbor] of match) {
    iterationContext.matchMap.atom.set(neighbor.atom, matchingNeighbor.atom);
    iterationContext.matchMap.bond.set(neighbor.bond, matchingNeighbor.bond);
  }
  for (let [neighbor, matchingNeighbor] of match) {
    if (!neighbor.bond.isEquivalent(matchingNeighbor.bond)) {
      errors.push(neighbor.bond.id);
    }
    if (!neighbor.atom.isEquivalent(matchingNeighbor.atom)) {
      errors.push(neighbor.atom.id);
    }
    if (neighbor.atom.neighbors.length != matchingNeighbor.atom.neighbors.length) {
      errors.push(neighbor.atom.id);
    }
    errors.push(...checkMapping(neighbor, matchingNeighbor, iterationContext, match));
    errors = [...new Set(errors)];
    if (currentBest && errors.length > currentBest) {
      return null;
    }
    const neighborRemainingNeighbors = neighbor.atom.neighbors.filter((nNeighbor) => {
      return !iterationContext.visitedAtoms.testMol.includes(nNeighbor.atom.id);
    });
    const matchingNeighborRemainingNeighbors = matchingNeighbor.atom.neighbors.filter((nNeighbor) => {
      return !iterationContext.visitedAtoms.controlMol.includes(nNeighbor.atom.id);
    });
    const possibleNeighborsCombinations = neighborRemainingNeighbors.length == matchingNeighborRemainingNeighbors.length ? [neighborRemainingNeighbors] : k_combinations(neighborRemainingNeighbors, neighborRemainingNeighbors.length > matchingNeighborRemainingNeighbors.length ? matchingNeighborRemainingNeighbors.length : neighborRemainingNeighbors.length);
    let bestComparison = void 0;
    for (let i = 0; i < possibleNeighborsCombinations.length; ++i) {
      let currComb = possibleNeighborsCombinations[i];
      for (let permutation of permute(currComb)) {
        if (permutation.length > 0) {
          const iterationContextCopy = iterationContext.createCopy();
          const testMatch = createMapFromArrays(permutation, matchingNeighborRemainingNeighbors);
          const permutationResultingErrors = compareMoleculeFromNeighbors(iterationContextCopy, testMatch, currentBest);
          if (!permutationResultingErrors) {
            continue;
          }
          if (!bestComparison || bestComparison.errors.length > permutationResultingErrors.length) {
            bestComparison = {
              errors: permutationResultingErrors,
              iterationContext: iterationContextCopy
            };
          }
        }
      }
    }
    if (bestComparison) {
      errors.push(...bestComparison.errors);
      iterationContext.absorb(bestComparison.iterationContext);
    }
  }
  return [...new Set(errors)];
}
function sortByOccurence(atoms) {
  const occurences = /* @__PURE__ */ new Map();
  for (const [id, atomNode] of atoms) {
    if (!occurences.has(atomNode.symbol)) {
      occurences.set(atomNode.symbol, 0);
    }
    occurences.set(atomNode.symbol, occurences.get(atomNode.symbol) + 1);
  }
  const priorities = /* @__PURE__ */ new Map();
  for (const [id, atomNode] of atoms) {
    priorities.set(atomNode, occurences.get(atomNode.symbol));
  }
  return Array.from(atoms.values()).sort((a, b) => {
    return priorities.get(a) - priorities.get(b);
  });
}
function compareMolecule(test, control) {
  const testMolGraph = new MolGraph(test);
  const controlMolGraph = new MolGraph(control);
  let bestComparison = void 0;
  const occurenceSortedAtomNodes = sortByOccurence(testMolGraph.atoms);
  for (const atomNode of occurenceSortedAtomNodes) {
    const matches = controlMolGraph.getMatching(atomNode);
    for (const match of matches) {
      const comparisonResult = compareMoleculeFromMatch(testMolGraph, atomNode.id, controlMolGraph, match.id);
      if (!bestComparison || comparisonResult && comparisonResult.length < bestComparison.length) {
        bestComparison = comparisonResult;
      }
    }
  }
  const feedback = bestComparison ? bestComparison : [...test.atoms.map((atom) => atom.id), ...test.bonds.map((bond) => bond.id)];
  const isFormulaCorrect = test.formula && control.formula ? test.formula == control.formula : false;
  return { feedback, isFormulaCorrect };
}
function getJsonFromKCJ(kcj) {
  const mol = Kekule.IO.loadFormatData(kcj, Kekule.IO.DataFormat.KEKULE_JSON);
  return exportJSON(mol);
}
var Viewer = class {
  constructor(htmlElement, kcj, displayMode) {
    this.DEFAULTZOOM = 1.75;
    this.kekuleViewer = new Kekule.ChemWidget.Viewer(htmlElement);
    this.kekuleViewer.setZoom(this.DEFAULTZOOM);
    this.kekuleViewer.setEnableEdit(false);
    this.kekuleViewer.setResizable(false);
    this.kekuleViewer.setToolbarPos(Kekule.Widget.Position.BOTTOM_LEFT);
    this.kekuleViewer.setToolButtons(["saveData", "molDisplayType", "zoomIn", "zoomOut"]);
    this.kekuleViewer.setEnableToolbar(true);
    if (displayMode && displayMode == "skeletal") {
      this.kekuleViewer.setMoleculeDisplayType(Kekule.Render.Molecule2DDisplayType.SKELETAL);
    } else {
      this.kekuleViewer.setMoleculeDisplayType(Kekule.Render.Molecule2DDisplayType.CONDENSED);
    }
    this._addRightClickScroll();
    this._addWheelZoom();
    this._addFormula();
    if (kcj) {
      this.loadKCJ(kcj);
    }
  }
  /**
   * Adds zoom commands on mouse wheel scroll up and down.
   */
  _addWheelZoom() {
  }
  /**
   * By default in kekule, scrolling is a button in the toolbar. 
   * Adds a scroll functionality on right click mouse button.
   */
  _addRightClickScroll() {
  }
  /**
   * Adds a div in bottom right corner of viewer that shows the formula of currently drawn molecules.
   * 
   */
  _addFormula() {
    const formulaDivClass = "FormulaDiv";
    const kekuleHtmlElement = this.kekuleViewer.getElement();
    this.formulaDiv = document.createElement("div");
    this.formulaDiv.className = formulaDivClass;
    kekuleHtmlElement.appendChild(this.formulaDiv);
  }
  /**
   * Gets the formula of drawn molecules and displays them
   */
  updateFormula() {
    this.formulaDiv.innerHTML = "";
    const formulas = this.getFormulaAsDiv();
    for (let i = 0; i < formulas.length; i += 1) {
      this.formulaDiv.appendChild(formulas[i]);
    }
  }
  /**
   * Get formula of drawn molecules (div)
   * 
   * @returns Array of formulas (div), one formula per drawn molecule
   */
  getFormulaAsDiv() {
    function createFormattedDivs(formula2) {
      var _a;
      const returnDiv = document.createElement("div");
      returnDiv.className = "Formula";
      formula2.items.sort((a, b) => {
        var _a2, _b;
        a = ((_a2 = a.items[0].anchorItem) != null ? _a2 : a.anchorItem).text;
        b = ((_b = b.items[0].anchorItem) != null ? _b : b.anchorItem).text;
        if (a == "C" || a == "H" && b != "C") {
          return -1;
        }
        if (b == "C" || b == "H" && a != "C") {
          return 1;
        }
        return a.localeCompare(b);
      });
      for (let i = 0, l = formula2.items.length; i < l; ++i) {
        const atom = formula2.items[i];
        let anchorItem = (_a = atom.items[0].anchorItem) != null ? _a : atom.anchorItem;
        returnDiv.appendChild(document.createTextNode(anchorItem.text));
        for (let j = 1, l2 = atom.items.length; j < l2; ++j) {
          const atomPart = atom.items[j];
          if (atomPart.textType == "subscript") {
            const sub = document.createElement("sub");
            sub.textContent = atomPart.text;
            returnDiv.appendChild(sub);
          }
          if (atomPart.textType == "superscript") {
            const sup = document.createElement("sup");
            sup.textContent = atomPart.text;
            returnDiv.appendChild(sup);
          }
        }
      }
      return returnDiv;
    }
    const formula = [];
    const children = this.kekuleViewer.getChemObj().getChildren();
    for (let i = 0; i < children.length; i += 1) {
      const elem = children[i];
      if (elem.calcFormula) {
        formula.push(createFormattedDivs(elem.calcFormula().getDisplayRichText()));
      }
    }
    return formula;
  }
  loadKCJ(kcj) {
    try {
      this.kekuleViewer.setChemObj(Kekule.IO.loadFormatData(kcj, Kekule.IO.DataFormat.KEKULE_JSON));
      this.kekuleViewer.setZoom(this.DEFAULTZOOM);
      this.updateFormula();
    } catch (error) {
      console.log(`Error while importing KCJ`, error);
    }
  }
  loadChemObj(chemObj) {
    try {
      this.kekuleViewer.setChemObj(chemObj);
      this.kekuleViewer.setZoom(this.DEFAULTZOOM);
      this.updateFormula();
    } catch (error) {
      console.log(`Error while importing chem object`, error);
    }
  }
  highlightFormula(color) {
    this.formulaDiv.style.color = color;
  }
  highlightElements(elements, color) {
    const chemObj = this.kekuleViewer.getChemObj().clone(true);
    const mol = chemObj.getChildren()[0];
    for (let i = 0; i < elements.length; ++i) {
      const currErrorId = elements[i];
      const elem = mol.getObjectById(currErrorId);
      let elemRenderOptions = elem.getRenderOptions();
      if (!elemRenderOptions) {
        elemRenderOptions = {};
        elem.setRenderOptions(elemRenderOptions);
      }
      elemRenderOptions.color = color;
      if (elem instanceof Kekule.Atom) {
        elemRenderOptions.useAtomSpecifiedColor = false;
      }
    }
    this.loadChemObj(chemObj);
  }
};

export { Composer, Viewer, compareMolecule, getJsonFromKCJ };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map



//////////////////
// WEBPACK FOOTER
// ./~/composer/dist/index.js
// module id = 47
// module chunks = 0